// frontend/src/components/formularios/campos/TablaMemoriaCompleja.jsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery
} from '@mui/material';

const TablaMemoriaCompleja = ({ campo, value = {}, onChange, disabled = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { configuracion } = campo;
  
  const handleCampoChange = (campoId, valor) => {
    const nuevoValor = {
      ...value,
      [campoId]: valor
    };
    onChange(nuevoValor);
  };

  const renderCelda = (columna) => {
    return (
      <TableCell 
        key={columna.id} 
        sx={{ 
          verticalAlign: 'top',
          borderRight: '1px solid',
          borderColor: 'divider',
          p: 1,
        }}
      >
        <TextField
          fullWidth
          multiline
          rows={columna.filas || 6}
          value={value[columna.id] || ''}
          onChange={(e) => handleCampoChange(columna.id, e.target.value)}
          disabled={disabled}
          variant="outlined"
          size="small"
          placeholder={`${columna.etiqueta}...`}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            }
          }}
        />
      </TableCell>
    );
  };

  return (
    <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        {campo.etiqueta || campo.label}
      </Typography>
      
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {/* Headers de la estructura jerárquica */}
              <TableCell 
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  minWidth: '120px'
                }}
              >
                Memoria sensorial
              </TableCell>
              <TableCell 
                colSpan={2}
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  minWidth: '200px'
                }}
              >
                Memoria por tiempo de almacenamiento
              </TableCell>
              <TableCell 
                colSpan={2}
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                Memoria a largo plazo
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Fila de subtipos */}
            <TableRow>
              {/* Memoria sensorial - subdividida en columnas */}
              <TableCell sx={{ p: 0, verticalAlign: 'top' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          backgroundColor: 'grey.50',
                          fontWeight: 500,
                          textAlign: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1
                        }}
                      >
                        M. Ecoica
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          backgroundColor: 'grey.50',
                          fontWeight: 500,
                          textAlign: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1
                        }}
                      >
                        M. Icónica
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ p: 1, width: '50%', height: '100%' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={13}
                          value={value['m_ecoica'] || ''}
                          onChange={(e) => handleCampoChange('m_ecoica', e.target.value)}
                          disabled={disabled}
                          variant="outlined"
                          size="small"
                          placeholder="M. Ecoica..."
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '100%',
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 1, width: '50%', height: '100%' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={13}
                          value={value['m_iconica'] || ''}
                          onChange={(e) => handleCampoChange('m_iconica', e.target.value)}
                          disabled={disabled}
                          variant="outlined"
                          size="small"
                          placeholder="M. Icónica..."
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '100%',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableCell>

              {/* Memoria por tiempo - subdividida en columnas */}
              <TableCell colSpan={2} sx={{ p: 0, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          backgroundColor: 'grey.50',
                          fontWeight: 500,
                          textAlign: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1
                        }}
                      >
                        Memoria a corto plazo
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          backgroundColor: 'grey.50',
                          fontWeight: 500,
                          textAlign: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1
                        }}
                      >
                        Memoria de trabajo
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ p: 1, width: '50%', height: '100%' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={13}
                          value={value['memoria_corto_plazo'] || ''}
                          onChange={(e) => handleCampoChange('memoria_corto_plazo', e.target.value)}
                          disabled={disabled}
                          variant="outlined"
                          size="small"
                          placeholder="Memoria a corto plazo..."
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '100%',
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 1, width: '50%', height: '100%' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={13}
                          value={value['memoria_trabajo'] || ''}
                          onChange={(e) => handleCampoChange('memoria_trabajo', e.target.value)}
                          disabled={disabled}
                          variant="outlined"
                          size="small"
                          placeholder="Memoria de trabajo..."
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '100%',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableCell>

              {/* Memoria a largo plazo - estructura compleja */}
              <TableCell colSpan={2} sx={{ p: 0, verticalAlign: 'top' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell 
                        colSpan={2}
                        sx={{ 
                          backgroundColor: 'grey.50',
                          fontWeight: 500,
                          textAlign: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1
                        }}
                      >
                        Memoria declarativa
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          backgroundColor: 'grey.100',
                          fontWeight: 500,
                          p: 1,
                          width: '50%'
                        }}
                      >
                        M. Semántica
                      </TableCell>
                      <TableCell sx={{ p: 1, width: '50%' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={value['m_semantica'] || ''}
                          onChange={(e) => handleCampoChange('m_semantica', e.target.value)}
                          disabled={disabled}
                          variant="outlined"
                          size="small"
                          placeholder="M. Semántica..."
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          backgroundColor: 'grey.100',
                          fontWeight: 500,
                          p: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        M. Episódica
                      </TableCell>
                      <TableCell sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={value['m_episodica'] || ''}
                          onChange={(e) => handleCampoChange('m_episodica', e.target.value)}
                          disabled={disabled}
                          variant="outlined"
                          size="small"
                          placeholder="M. Episódica..."
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell 
                        colSpan={2}
                        sx={{ 
                          backgroundColor: 'grey.50',
                          fontWeight: 500,
                          textAlign: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1
                        }}
                      >
                        Memoria procedimental o no declarativa
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ p: 1 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          value={value['memoria_procedimental'] || ''}
                          onChange={(e) => handleCampoChange('memoria_procedimental', e.target.value)}
                          disabled={disabled}
                          variant="outlined"
                          size="small"
                          placeholder="Memoria procedimental o no declarativa..."
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      {isMobile && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Desliza horizontalmente para ver todas las columnas
        </Typography>
      )}
    </Paper>
  );
};

export default TablaMemoriaCompleja;